import React from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  ReferenceInput,
  DateInput,
  NumberInput,
  required,
  TopToolbar,
  ListButton,
  ShowButton,
  DeleteButton,
  AutocompleteInput,
  useRecordContext,
} from 'react-admin';
import { Grid, Card, CardContent, Typography, Box, Alert } from '@mui/material';
import {
  Assignment as TaskIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Schedule as ScheduleIcon,
  Flag as PriorityIcon,
  AccessTime as TimeIcon,
  CheckCircle as CompletedIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { PageTitle } from '../../components/PageTitle';
import { TaskEntitySelector } from '../../components/TaskEntitySelector';
import { TaskTagsInput } from '../../components/TaskTagsInput';
import { HoursMinutesInput } from '../../components/HoursMinutesInput';

const EditActions = () => (
  <TopToolbar>
    <ListButton />
    <ShowButton />
    <DeleteButton />
  </TopToolbar>
);

const statusChoices = [
  { id: 'Todo', name: 'To Do' },
  { id: 'InProgress', name: 'In Progress' },
  { id: 'InReview', name: 'In Review' },
  { id: 'Blocked', name: 'Blocked' },
  { id: 'Done', name: 'Done' },
  { id: 'Cancelled', name: 'Cancelled' },
];

const priorityChoices = [
  { id: 'Lowest', name: 'Lowest' },
  { id: 'Low', name: 'Low' },
  { id: 'Medium', name: 'Medium' },
  { id: 'High', name: 'High' },
  { id: 'Highest', name: 'Highest' },
  { id: 'Critical', name: 'Critical' },
];

const urgencyChoices = [
  { id: 'Low', name: 'Low' },
  { id: 'Normal', name: 'Normal' },
  { id: 'Urgent', name: 'Urgent' },
];



const TaskStatusAlert = () => {
  const record = useRecordContext();
  
  if (!record) {return null;}
  
  if (record.status === 'Done' && record.completedAt) {
    return (
      <Alert severity="success" sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <CompletedIcon />
          <Typography>
            Task completed on {new Date(record.completedAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
      </Alert>
    );
  }
  
  if (record.dueDate && new Date(record.dueDate) < new Date() && record.status !== 'Done') {
    const daysOverdue = Math.ceil((new Date().getTime() - new Date(record.dueDate).getTime()) / (1000 * 3600 * 24));
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography>
          This task is {daysOverdue} day{daysOverdue > 1 ? 's' : ''} overdue!
        </Typography>
      </Alert>
    );
  }
  
  return null;
};

export const TaskEdit = () => {
  const handleSuccess = (data: any) => {
    console.log('Task update success:', data);
  };

  const handleError = (error: any) => {
    console.error('Task update error:', error);
  };

  const transform = (data: any) => {
    console.log('Transform called with data:', data);
    // Remove any undefined or null values
    const cleanData = Object.keys(data).reduce((acc: any, key) => {
      if (data[key] !== undefined && data[key] !== null) {
        acc[key] = data[key];
      }
      return acc;
    }, {});
    console.log('Sending cleaned data:', cleanData);
    return cleanData;
  };

  return (
    <>
      <PageTitle title="Edit Task" titleKey="pages.editTask" icon={<TaskIcon />} />
      <Edit 
        resource="tasks"
        actions={<EditActions />}
        mutationMode="pessimistic"
        mutationOptions={{
          onSuccess: handleSuccess,
          onError: handleError,
        }}
        transform={transform}
      >
        <SimpleForm 
          mode="onChange"
          reValidateMode="onChange"
        >
          <TaskStatusAlert />
          
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <TaskIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextInput
                        source="title"
                        label="Task Title"
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextInput
                        source="description"
                        label="Description"
                        multiline
                        rows={4}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Status and Priority */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <PriorityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Status & Priority
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <SelectInput
                        source="status"
                        label="Status"
                        choices={statusChoices}
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SelectInput
                        source="priority"
                        label="Priority"
                        choices={priorityChoices}
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SelectInput
                        source="urgency"
                        label="Urgency"
                        choices={urgencyChoices}
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Assignment and Category */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Assignment
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <ReferenceInput
                        source="assigneeId"
                        reference="users"
                        label="Assignee"
                      >
                        <AutocompleteInput
                          optionText="name"
                          fullWidth
                        />
                      </ReferenceInput>
                    </Grid>
                    <Grid item xs={12}>
                      <SelectInput
                        source="categoryId"
                        label="Category"
                        choices={[
                          { id: 'maintenance', name: 'Maintenance' },
                          { id: 'inspection', name: 'Inspection' },
                          { id: 'insurance', name: 'Insurance' },
                          { id: 'registration', name: 'Registration' },
                          { id: 'license', name: 'License' },
                          { id: 'permit', name: 'Permit' },
                          { id: 'fuel-tax', name: 'Fuel Tax' },
                          { id: 'dot-compliance', name: 'DOT Compliance' },
                          { id: 'training', name: 'Training' },
                          { id: 'warranty', name: 'Warranty' },
                          { id: 'customer-service', name: 'Customer Service' },
                          { id: 'administrative', name: 'Administrative' },
                          { id: 'sales', name: 'Sales & Marketing' },
                          { id: 'billing', name: 'Billing & Finance' },
                        ]}
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Scheduling */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Scheduling
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <DateInput
                        source="startDate"
                        label="Start Date"
                        fullWidth
                        parse={(value) => value ? new Date(value).toISOString().split('T')[0] : null}
                        format={(value) => value ? new Date(value).toISOString().split('T')[0] : ''}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DateInput
                        source="dueDate"
                        label="Due Date"
                        fullWidth
                        parse={(value) => value ? new Date(value).toISOString().split('T')[0] : null}
                        format={(value) => value ? new Date(value).toISOString().split('T')[0] : ''}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DateInput
                        source="completedAt"
                        label="Completed At"
                        fullWidth
                        parse={(value) => value ? new Date(value).toISOString() : null}
                        format={(value) => value ? new Date(value).toISOString().slice(0, 16) : ''}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Time Tracking */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <TimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Time Tracking
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <HoursMinutesInput
                        source="estimatedHours"
                        label="Estimated Time"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <HoursMinutesInput
                        source="actualHours"
                        label="Actual Time"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <HoursMinutesInput
                        source="duration"
                        label="Planned Duration"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Entity Association */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <LinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Related Entity
                  </Typography>
                  <Grid container spacing={2}>
                    <TaskEntitySelector />
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Hierarchy */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Task Hierarchy
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <ReferenceInput
                        source="parentTaskId"
                        reference="tasks"
                        label="Parent Task"
                      >
                        <AutocompleteInput
                          optionText="title"
                          fullWidth
                        />
                      </ReferenceInput>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Tags */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <TaskTagsInput source="tags" />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </SimpleForm>
      </Edit>
    </>
  );
}; 